import { createContext, useCallback, useContext, useMemo, useState, useLayoutEffect, type ReactNode } from 'react';

import { readCachedSettings, settingsCache, subscribeSettings, updateSettings } from '@/lib/settings/cache';

import { KIND_GROUP } from '@/lib/plates/groups';
import type { PlateGroup } from '@/lib/plates/types';

export type PlatesMode = 'plates' | 'plain';
const GROUPS: readonly PlateGroup[] = ['Text', 'Media', 'Data', 'Diagram', 'Interactive'];
const stored = () => readCachedSettings(settingsCache.subject).elements;
const storedWords = () => new Set(readCachedSettings(settingsCache.subject).elements_words);

interface PlatesModeValue {
  mode: PlatesMode;
  passive: boolean;
  setMode: (mode: PlatesMode) => void;
  /** Families kept as their words while elements are drawn. */
  asWords: ReadonlySet<PlateGroup>;
  setAsWords: (group: PlateGroup, words: boolean) => void;
  /** Whether this reader has elements of this kind drawn; the caller still checks it can draw the kind. */
  draws: (kind: string) => boolean;
}

const drawsWith = (mode: PlatesMode, asWords: ReadonlySet<PlateGroup>) => (kind: string) => {
  const group = KIND_GROUP[kind];
  return mode === 'plates' && !(group && asWords.has(group));
};

const Ctx = createContext<PlatesModeValue>({ mode: 'plain', passive: false, setMode: () => {}, asWords: new Set(), setAsWords: () => {}, draws: () => false });
const PASSIVE: PlatesModeValue = { mode: 'plain', passive: true, setMode: () => {}, asWords: new Set(), setAsWords: () => {}, draws: () => false };

export function PassivePlatesModeProvider({ children }: { children: ReactNode }) {
  return <Ctx.Provider value={PASSIVE}>{children}</Ctx.Provider>;
}

export function PlatesModeProvider({ children, initial }: { children: ReactNode; initial?: PlatesMode }) {
  const [mode, setModeState] = useState<PlatesMode>(() => initial ?? stored());
  const [asWords, setAsWordsState] = useState<ReadonlySet<PlateGroup>>(() => (initial ? new Set() : storedWords()));
  useLayoutEffect(() => {
    const refresh = () => {
      setModeState(settingsCache.value.elements);
      setAsWordsState(new Set(settingsCache.value.elements_words));
    };
    if (!initial) {
      setModeState(stored());
      setAsWordsState(storedWords());
    }
    return subscribeSettings(refresh);
  }, [initial]);
  const setMode = useCallback((next: PlatesMode) => {
    setModeState(next);
    updateSettings({ elements: next });
  }, []);
  const setAsWords = useCallback((group: PlateGroup, words: boolean) => {
    const next = new Set(asWords);
    if (words) next.add(group);
    else next.delete(group);
    setAsWordsState(next);
    updateSettings({ elements_words: GROUPS.filter(g => next.has(g)) });
  }, [asWords]);
  const value = useMemo(() => ({ mode, passive: false, setMode, asWords, setAsWords, draws: drawsWith(mode, asWords) }), [mode, setMode, asWords, setAsWords]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePlatesMode = () => useContext(Ctx);
